import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { can } from '../../api/profile';
import {
  FALLBACK_CONTACT_SETTINGS,
  getPublicContactSettings,
  updateContactSettings,
  type EditableContactSettings,
} from '../../features/contactSettings';
import { ApiError } from '../../shared/api';
import { Card, DashboardPageHeader } from '../../shared/ui';
import ClassDescriptionEditor from '../../components/dashboard/ClassDescriptionEditor';

const DonorSectionPage: React.FC = () => {
  const { profile } = useAuth();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<EditableContactSettings>({
    contactNumber: '',
    whatsappNumber: '',
    address: '',
    emailAddress: '',
    accountName: '',
    accountNumber: '',
    bankName: '',
    branchName: '',
    branchCode: '',
    reference: '',
    footerLinksJson: null,
    donorTitle: '',
    donorDescriptionHtml: '',
  });

  const canView = !!profile && can(profile, 'contactSettings', 'view');
  const canEdit = !!profile && can(profile, 'contactSettings', 'edit');

  useEffect(() => {
    if (!canView) return;
    void load();
  }, [canView]);

  const load = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getPublicContactSettings();
      setSettings({
        contactNumber: response.contactNumber,
        whatsappNumber: response.whatsappNumber,
        address: response.address,
        emailAddress: response.emailAddress,
        accountName: response.accountName,
        accountNumber: response.accountNumber,
        bankName: response.bankName,
        branchName: response.branchName,
        branchCode: response.branchCode,
        reference: response.reference ?? '',
        footerLinksJson: response.footerLinksJson ?? null,
        donorTitle: response.donorTitle,
        donorDescriptionHtml: response.donorDescriptionHtml,
      });
    } catch (err) {
      console.error(err);
      setSettings({
        contactNumber: FALLBACK_CONTACT_SETTINGS.contactNumber,
        whatsappNumber: FALLBACK_CONTACT_SETTINGS.whatsappNumber,
        address: FALLBACK_CONTACT_SETTINGS.address,
        emailAddress: FALLBACK_CONTACT_SETTINGS.emailAddress,
        accountName: FALLBACK_CONTACT_SETTINGS.accountName,
        accountNumber: FALLBACK_CONTACT_SETTINGS.accountNumber,
        bankName: FALLBACK_CONTACT_SETTINGS.bankName,
        branchName: FALLBACK_CONTACT_SETTINGS.branchName,
        branchCode: FALLBACK_CONTACT_SETTINGS.branchCode,
        reference: FALLBACK_CONTACT_SETTINGS.reference ?? '',
        footerLinksJson: FALLBACK_CONTACT_SETTINGS.footerLinksJson ?? null,
        donorTitle: FALLBACK_CONTACT_SETTINGS.donorTitle,
        donorDescriptionHtml: FALLBACK_CONTACT_SETTINGS.donorDescriptionHtml,
      });
      setError('Failed to load donor section settings. Showing fallback defaults.');
    } finally {
      setIsLoading(false);
    }
  };

  const onSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canEdit) return;
    setError(null);
    try {
      setIsSaving(true);
      const updated = await updateContactSettings({
        ...settings,
        reference: settings.reference?.trim() ? settings.reference.trim() : null,
        donorTitle: settings.donorTitle.trim(),
      });
      setSettings({
        contactNumber: updated.contactNumber,
        whatsappNumber: updated.whatsappNumber,
        address: updated.address,
        emailAddress: updated.emailAddress,
        accountName: updated.accountName,
        accountNumber: updated.accountNumber,
        bankName: updated.bankName,
        branchName: updated.branchName,
        branchCode: updated.branchCode,
        reference: updated.reference ?? '',
        footerLinksJson: updated.footerLinksJson ?? null,
        donorTitle: updated.donorTitle,
        donorDescriptionHtml: updated.donorDescriptionHtml,
      });
      showToast('Donor section updated successfully.');
    } catch (err) {
      console.error(err);
      if (err instanceof ApiError && err.status === 400) {
        setError('Some details are invalid. Please check and try again.');
      } else if (err instanceof ApiError && err.status === 401) {
        setError('Your session has expired. Please sign in again.');
      } else {
        setError('Failed to save donor section. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (!canView) {
    return (
      <div className="row">
        <div className="col-12">
          <div className="alert alert-warning" role="alert">
            <i className="bi bi-shield-exclamation me-2" />
            <strong>Access Restricted:</strong> You do not have permission to view contact and donor settings.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-12">
        <DashboardPageHeader
          title="Donor Section"
          description="Title and rich text for the donors panel on the public timeline."
          icon="bi bi-heart-fill"
        />

        {error && (
          <div className="alert alert-danger mb-3" role="alert">
            <i className="bi bi-exclamation-triangle me-2" />
            {error}
          </div>
        )}
        {canView && !canEdit && (
          <div className="alert alert-info mb-3" role="alert">
            <i className="bi bi-info-circle me-2" />
            You can view this section but your role does not allow editing. Ask an administrator to grant
            Contact settings — Edit if you need to make changes.
          </div>
        )}

        <Card title="Donor Message Settings" icon="bi bi-pencil-square">
          {isLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted mb-0">Loading settings...</p>
            </div>
          ) : (
            <form onSubmit={onSave}>
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label">Title</label>
                  <input
                    className="form-control"
                    value={settings.donorTitle}
                    onChange={(e) =>
                      setSettings((prev) => ({ ...prev, donorTitle: e.target.value }))
                    }
                    required
                    disabled={!canEdit}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Description</label>
                  <ClassDescriptionEditor
                    html={settings.donorDescriptionHtml}
                    onHtmlChange={(html) =>
                      setSettings((prev) => ({ ...prev, donorDescriptionHtml: html }))
                    }
                    readOnly={!canEdit}
                  />
                </div>
              </div>

              <div className="mt-4 d-flex justify-content-end">
                <button className="btn btn-primary" type="submit" disabled={isSaving || !canEdit}>
                  {isSaving ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
};

export default DonorSectionPage;
